import type {ReactElement} from 'react';
import {useState} from 'react';
import {useAppSelector} from '@/hooks';
import {Text, TextSize} from '@Components/text';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import {InputField} from '@Components/input-field-v2';
import {InputFieldSize, InputFieldState, InputFieldType} from '@Components/input-field-v2/input-field.types';
import type {CheckoutStepProps} from '@Components/checkout-flow/checkout-flow.types';
import {addCheckoutEmailAddress} from '@Components/checkout-flow/checkout-flow-thunk';
import {getStepAfterEmailAdded} from '@Components/checkout-flow/checkout-flow.library';
import {CheckoutInfoMessage} from '@Components/checkout-flow/components/checkout-info-message/checkout-info-message';
import {CheckoutInfoMessageTone} from '@Components/checkout-flow/components/checkout-info-message/checkout-info-message.types';
import styles from './email-required-step.module.scss';

export function EmailRequiredStep({controller}: CheckoutStepProps): ReactElement {
  const dialogData = useAppSelector((state) => state.checkoutFlow.dialogData);
  const [emailAddress, setEmailAddress] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const canContinue = emailAddress.trim() !== '' && !isSubmitting;

  const submitEmailAddress = async (): Promise<void> => {
    if (!canContinue || dialogData === null) {
      return;
    }
    setIsSubmitting(true);
    setHasError(false);
    try {
      await addCheckoutEmailAddress(emailAddress.trim());
      controller.goToStep(getStepAfterEmailAdded(dialogData));
    } catch {
      setHasError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getErrorMessage = (): ReactElement | null => {
    if (!hasError) {
      return null;
    }
    return <CheckoutInfoMessage message={window.i18next.t('pmwjs_failed_to_add_email_please_enter_valid_email_and_try_again')} tone={CheckoutInfoMessageTone.DANGER} />;
  };

  return (
    <div className={styles.container}>
      <Text val={window.i18next.t('pmwjs_email_address_required_description')} size={TextSize.MD} className="content-body spacing-m-b-4" />
      <div className={styles.inputRow}>
        <InputField
          input={emailAddress}
          inputType={InputFieldType.EMAIL}
          size={InputFieldSize.MEDIUM}
          placeholder={window.i18next.t('pmwjs_email')}
          state={hasError ? InputFieldState.ERROR : InputFieldState.DEFAULT}
          className={styles.input}
          isControlled
          onInputChange={(value): void => {
            setEmailAddress(String(value));
          }}
          onInputSubmit={(): void => {
            void submitEmailAddress();
          }}
        />
        <Button type={ButtonType.PRIMARY} size={ButtonSize.LARGE} text={window.i18next.t('pmwjs_continue')} disabled={!canContinue} isLoading={isSubmitting} onClick={submitEmailAddress} />
      </div>
      {getErrorMessage()}
    </div>
  );
}
